'use es6';

import I18n from 'I18n';
import PropertyGroupRecord from 'customer-data-objects/property/PropertyGroupRecord';
import PropertyRecord from 'customer-data-objects/property/PropertyRecord';
import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { Map as ImmutableMap, Set as ImmutableSet, List } from 'immutable';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as dataTypes from 'reporting-data/constants/dataTypes';
import { DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES } from 'reporting-data/constants/objectCoordinates';
import { CURRENCY, DURATION } from 'reporting-data/constants/property-types';
import { getCrmObjectName, isSupportedCrmObject, getCrmObjectLabelByName } from 'reporting-data/crmObjects/utils';
import getPropertyGroups from 'reporting-data/properties/groups';
import { isBlocklistedForFiltering } from 'reporting-data/public/blocklisted-for-filtering';
import { isExternalDataType } from 'reporting-data/report/externalReportGetters';
import { getCustomWidgetType, getDataTypeFromConfig } from 'reporting-data/report/reportGetters';
import { isMultiConfigReport, isReportWithPlugins } from 'reporting-data/tsTypes/reportTypes';
import { getDisplayParams } from 'reporting-data/report/configReportGetters';
import { fetchDataSystemMetadata } from 'reporting-data/v2/externalDataSystemMetadata/fetchMetadata';
import ReportingFilterEditor from 'reporting-enablement/components/ReportingFilterEditor';
import { waitForPromises } from 'reporting-ui-components/lib/waitForPromises';
import { RaiseError } from 'reporting-ui-components/trap/ErrorTrap';
import { RaiseLoading } from 'reporting-ui-components/trap/LoadingTrap';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import compose from 'transmute/compose';
import filter from 'transmute/filter';
import get from 'transmute/get';
import indexBy from 'transmute/indexBy';
import map from 'transmute/map';
import memoize from 'transmute/memoize';
import once from 'transmute/once';
import valueSeq from 'transmute/valueSeq';
import { isFilterSchemaMatch } from './QuickFilterEditor/SingleObjectQuickFilterEditor';
import { checkCanApplySavedFilters } from './canApplySavedFilters';
import { useQuickFilterSchemas } from './common/useEditorComponents';
import { getIsSecondaryDateRangeCustomFilter } from './common/dateRangeHelpers';
import { ErrorBoundary } from 'reporting-ui-components/reports/decorators/ErrorBoundaryDecorator';
import ErrorBoundaryErrorComponent, { ErrorTypes } from './../../monitoring/ErrorBoundaryErrorComponent';
import { Metrics } from '../../monitoring/Metrics';
import { dateDurations } from 'reporting-data/constants/dateDurations';
import { DEALS } from 'reporting-data/constants/dataTypes/inboundDb';
import * as ExternalOptionTypes from 'customer-data-objects/property/ExternalOptionTypes';
import { logMessage } from 'reporting-data/monitoring/logger';
import { jsx as _jsx } from "react/jsx-runtime";
const SUPPORTED_PROPERTY_TYPES = ImmutableSet(valueSeq(PropertyTypes));
const DISPOSITION_PROPERTY = once(() => PropertyRecord.fromJS({
  name: 'hs_call_disposition',
  label: I18n.text('reporting-data.properties.call.disposition'),
  type: 'enumeration'
}));
const TASK_STATUS_PROPERTY = once(() => PropertyRecord.fromJS({
  name: 'hs_task_status',
  label: I18n.text('reporting-data.properties.tasks.taskStatus.label'),
  type: 'enumeration',
  options: ['NOT_STARTED', 'WAITING', 'IN_PROGRESS', 'COMPLETED', 'DEFERRED'].map(value => ({
    value,
    label: I18n.text(`reporting-data.properties.tasks.taskStatus.options.${value}`)
  }))
}));
const fieldFilterOverrides = {
  [dataTypes.ENGAGEMENT]: {
    engagementInfo: {
      enumerationProperties: ['engagement.modifiedBy', 'hs_modified_by', 'engagement.ownerId', 'hubspot_owner_id', 'engagement.teamId', 'hubspot_team_id', 'engagement.createdBy', 'hs_created_by'],
      additionalPropertyBuilder: () => [DISPOSITION_PROPERTY(), TASK_STATUS_PROPERTY()]
    }
  },
  [DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES.get(dataTypes.DEAL_CREATE_ATTRIBUTION)]: {
    attributioncampaigninformation: {
      enumerationProperties: ['hs_campaign_guid']
    }
  }
};
const dataTypesWithIncorrectReferences = [DEALS, '0-3'];
const reportingNonFrameworkProperties = {
  'associations.company': 'COMPANY',
  'associations.contact': 'CONTACT'
};
export const overridePropertyReferenceType = properties => {
  return properties.map(property => {
    const propertyName = property.get('name');
    if (reportingNonFrameworkProperties[propertyName]) {
      return property.set('externalOptionsReferenceType', reportingNonFrameworkProperties[propertyName]);
    }
    return property;
  });
};
const fixListPropertyName = properties => {
  // Filters component migrate the ilsListMemberships.listId to hs_crm_search.ilsListIds
  // hs_crm_search.ilsListIds is not included in the list of properties and the filters is removed
  // Context:https://hubspot.slack.com/archives/CA3JB5WLT/p1744203611555959
  return properties.map(property => {
    const propertyName = property.get('name');
    if (propertyName === 'ilsListMemberships.listId') {
      return property.set('name', 'hs_crm_search.ilsListIds').set('externalOptionsReferenceType', ExternalOptionTypes.INBOUND_DB_LIST);
    }
    return property;
  });
};
const overridePropertyGroupReferences = (groups, dataType) => {
  if (dataTypesWithIncorrectReferences.includes(dataType)) {
    // This override fixes an issue where the association.company reference hydrated with
    // deals instead of companies due to multiple issues -
    // 1. Reporting use legacy Property record that doesn't pass through all the details needed by reference resolvers (https://hubspot.slack.com/archives/C06ECPKHDUG/p1734711696558879?thread_ts=1734709153.662799&cid=C06ECPKHDUG)
    // 2. Filters can't automatically resolve non framework properties (like associations.company)
    // 3. BE update the references to in metadata store, that caused the issue by setting partial external options,
    //    but removing the references caused a near critsit (https://hubspot.slack.com/archives/C50LV5B97/p1734731779249039) as BE is
    //    using the new fields to hydrate labels
    // This change limits dropping hte externalOptions to filters.
    // This can be removed once the new filters are rolled out.
    return groups.map(group => {
      return group.update('properties', properties => {
        return [overridePropertyReferenceType, fixListPropertyName].reduce((updatedProperties, overrideFunction) => {
          return overrideFunction(updatedProperties, dataType);
        }, properties);
      });
    });
  }
  return groups;
};
const applyFieldFilterOverrides = (groups, dataType) => {
  const updatedGroups = overridePropertyGroupReferences(groups, dataType);
  const overrides = fieldFilterOverrides[dataType];
  if (overrides) {
    return updatedGroups.map(group => {
      const groupOverrides = overrides[group.name];
      if (groupOverrides) {
        return group.update('properties', properties => {
          if (groupOverrides.additionalPropertyBuilder) {
            properties = properties.push(...groupOverrides.additionalPropertyBuilder());
          }
          if (groupOverrides.enumerationProperties) {
            properties = properties.map(property => groupOverrides.enumerationProperties.includes(property.name) ? property.set('type', 'enumeration') : property);
          }
          return properties;
        });
      }
      return group;
    });
  }
  return updatedGroups;
};
const FILTERABLE = ImmutableSet([dataTypes.DEALS, dataTypes.CONTACTS, dataTypes.COMPANIES, dataTypes.ENGAGEMENT, dataTypes.TICKETS, dataTypes.LINE_ITEMS, dataTypes.FEEDBACK_SUBMISSIONS]);
const propertyFixesByType = ImmutableMap({
  [DURATION]: {
    type: 'number'
  },
  [CURRENCY]: {
    type: 'number',
    showCurrencySymbol: true
  }
});
const getFilters = layer => layer.getIn(['filters', 'custom'], List());
export const getFields = memoize((getPropertiesGroupPromise, dataType, excludedFields = []) => {
  return getPropertiesGroupPromise.then(propertyGroups => {
    let groups = propertyGroups.map(group => PropertyGroupRecord.fromJS(group.update('properties', compose(filter(property => SUPPORTED_PROPERTY_TYPES.has(property.get('type')) && (!dataType || !isBlocklistedForFiltering(dataType, false, property)) && !excludedFields.includes(property.get('name'))), map(property => propertyFixesByType.has(property.get('type')) ? property.merge(propertyFixesByType.get(property.get('type'))) : property))).toJS()));
    groups = applyFieldFilterOverrides(groups, dataType);
    return {
      fieldGroups: indexBy(get('name'), groups),
      fields: indexBy(get('name'), groups.map(get('properties')).flatten(1))
    };
  });
});
export const getDataTypeLabel = memoize((dataType, customGetPropertyGroups) => {
  if (customGetPropertyGroups) {
    return Promise.resolve(I18n.text('reporting-ui-components.filterEditor.familyName.custom'));
  }
  if (isSupportedCrmObject(dataType)) {
    return getCrmObjectName(dataType);
  }
  if (isExternalDataType(dataType)) {
    return fetchDataSystemMetadata(dataType).then(metadata => {
      return metadata && metadata.displayLabel.value;
    });
  }
  if (dataType.startsWith('e_')) {
    return getCrmObjectLabelByName(dataType);
  }
  return Promise.resolve(I18n.text(`reporting-ui-components.dataType.${dataType}.single`));
});
const getHiddenAndDisplayedFilters = (quickFilterSchemas, config, customWidgetType, displayParams) => {
  const configFilters = getFilters(config);
  return configFilters.reduce((memo, customFilter) => {
    // hide custom filters used to populate secondary date range editor
    if (getIsSecondaryDateRangeCustomFilter(customFilter, config, customWidgetType, displayParams)) {
      return Object.assign({}, memo, {
        hiddenFilters: memo.hiddenFilters.push(customFilter)
      });
    }

    // hide custom filters used to populate quick filters
    const match = quickFilterSchemas.find(schema => isFilterSchemaMatch(customFilter, schema, customWidgetType));
    const category = match ? 'hiddenFilters' : 'displayedFilters';
    return Object.assign({}, memo, {
      [category]: memo[category].push(customFilter)
    });
  }, {
    hiddenFilters: List(),
    displayedFilters: List()
  });
};
const FilterEditorRenderer = ({
  appUsageTracker,
  report,
  onChange,
  options,
  showHeaderLabel = true,
  config,
  configName,
  excludedFields,
  getPropertyGroups: customGetPropertyGroups,
  shouldHideQuickFilterValues = false
}) => {
  const dataType = getDataTypeFromConfig(config);
  const quickFilterSchemas = useQuickFilterSchemas(report);
  const [fieldInfo, setFieldInfoPromise] = useState({
    status: 'pending'
  });
  const [dataTypeLabel, setDataTypeLabel] = useState({
    status: 'pending'
  });
  const customWidgetType = getCustomWidgetType(report);
  useEffect(() => {
    const getPropertyGroupsPromise = customGetPropertyGroups ? customGetPropertyGroups(report) : getPropertyGroups(dataType).then(groups => {
      return groups.map(group => group.update('properties', properties => {
        let updatedProperties = properties;
        if (dataType === '0-74') {
          const index = properties.findIndex(prop => prop.get('name') === 'hs_goal_target_currency_code');
          if (index > -1) {
            const field = properties.get(index);
            if (field) {
              updatedProperties = updatedProperties.set(index, field.set('referencedObjectType', 'MULTI_CURRENCY_CURRENCY_CODE'));
            }
          }
        }
        return updatedProperties.filter(property => {
          return !dateDurations.includes(property.get('dateDisplayHint'));
        });
      }));
    });
    waitForPromises({
      fieldInfo: {
        setStateFunction: setFieldInfoPromise,
        promise: getFields(getPropertyGroupsPromise, dataType, excludedFields)
      },
      dataTypeLabel: {
        setStateFunction: setDataTypeLabel,
        promise: getDataTypeLabel(dataType, customGetPropertyGroups)
      }
    });
  }, [dataType, excludedFields, report, customGetPropertyGroups]);
  const headerLabel = useMemo(() => {
    if (dataTypeLabel.status !== 'resolved') {
      return '';
    }
    return I18n.text('reporting-ui-components.filterEditor.title', {
      dataTypeLabel: dataTypeLabel.value
    });
  }, [dataTypeLabel]);
  const getFilterFamilyName = useCallback(() => {
    return I18n.text('reporting-ui-components.filterEditor.familyName.custom');
  }, []);
  useEffect(() => {
    Metrics.counter('SRV-filters-renders', {
      reportType: 'config'
    }).increment();
    // Log usage of filter editor to Sentry

    logMessage({
      message: 'Legacy Filter Editor Usage',
      extra: {
        report
      }
    });
  }, [report]);
  const displayParams = getDisplayParams(report);
  const {
    hiddenFilters,
    displayedFilters
  } = useMemo(() => {
    if (shouldHideQuickFilterValues) {
      return getHiddenAndDisplayedFilters(quickFilterSchemas, config, customWidgetType, displayParams);
    }
    return {
      hiddenFilters: List(),
      displayedFilters: getFilters(config)
    };
  }, [config, customWidgetType, displayParams, quickFilterSchemas, shouldHideQuickFilterValues]);
  if (fieldInfo.status === 'rejected' || dataTypeLabel.status === 'rejected') {
    return /*#__PURE__*/_jsx(RaiseError, {
      error: fieldInfo.error || ''
    });
  }
  if (fieldInfo.status === 'pending' || dataTypeLabel.status === 'pending') {
    return /*#__PURE__*/_jsx(RaiseLoading, {});
  }
  return /*#__PURE__*/_jsx(ReportingFilterEditor, {
    canApplySavedFilters: checkCanApplySavedFilters(report),
    dataType: dataType,
    headerLabel: showHeaderLabel && headerLabel,
    filters: displayedFilters,
    onChange: filters => {
      const updatedFilters = hiddenFilters.concat(filters);
      return configName === 'config' ? onChange(report.setIn(['config', 'filters', 'custom'], updatedFilters)) : onChange(report.setIn(['reportConfigs', configName, 'filters', 'custom'], updatedFilters));
    },
    appUsageTracker: appUsageTracker,
    readOnly: options.readOnly,
    hasBuilderAccess: options.builderAccess,
    fields: fieldInfo.value.fields,
    fieldGroups: fieldInfo.value.fieldGroups,
    getFilterFamilyName: customGetPropertyGroups || isExternalDataType(dataType) ? getFilterFamilyName : undefined,
    report: report
  });
};
const FilterEditor = props => {
  const {
    report,
    isV2FilterExperience
  } = props;
  let configs = ImmutableMap({
    config: report.get('config')
  });
  if (isMultiConfigReport(report)) {
    configs = configs.merge(report.get('reportConfigs'));
  }
  const ErrorComponent = useCallback(() => /*#__PURE__*/_jsx(ErrorBoundaryErrorComponent, {
    type: ErrorTypes.FILTERS,
    "data-test-id": "reporting-filter-editor-error-component"
  }), []);
  if (isV2FilterExperience) {
    // This editor isn't supported in V2 Filters experience.
    return null;
  }
  return configs.keySeq().map(configName => /*#__PURE__*/_jsx(ErrorBoundary, {
    errorBoundaryName: "reporting-filter-editor",
    report: report,
    ErrorComponent: ErrorComponent,
    children: /*#__PURE__*/_jsx(FilterEditorRenderer, Object.assign({}, props, {
      configName: configName,
      config: configs.get(configName)
    }))
  }, `${configName}`));
};
FilterEditor.propTypes = {
  appUsageTracker: PropTypes.object,
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({
    readOnly: PropTypes.bool,
    builderAccess: PropTypes.bool
  }).isRequired,
  excludedFields: PropTypes.array,
  showHeaderLabel: PropTypes.bool,
  shouldHideQuickFilterValues: PropTypes.bool
};
FilterEditor.isUsableForReport = (report, options) => {
  if (!report.get('config', false)) return false;
  const filters = getFilters(report.get('config'));
  const dataType = getDataTypeFromConfig(report.get('config'));
  return (!options.readOnly || filters.size > 0) && FILTERABLE.has(dataType) || [dataTypes.ATTRIBUTION_TOUCH_POINTS, dataTypes.CONTACT_CREATE_ATTRIBUTION].includes(dataType) || isSupportedCrmObject(dataType) || isReportWithPlugins(report) || isExternalDataType(dataType);
};
FilterEditor.isFilterEditor = true;
export const __TESTABLE__ = {
  FilterEditorRenderer
};
export default FilterEditor;